<template>
	<aside>
		<template
			v-if="
				!chatStore.currentChannel?.canPatientWrite ||
				chatStore.currentChannel.isPatientLocked
			"
		>
			<!-- user cannot send messages -->
			<div
				class="flex items-center justify-center gap-2 font-medium text-red py-2"
			>
				<Unlock class="size-6"></Unlock>
				<p>Do této konverzace nemůžete přispívat</p>
			</div>
		</template>
		<template v-else>
			<form action="#" @submit.prevent="submit()">
				<div
					:class="[
						'dropzone flex items-end',
						isDragOver
							? 'outline-2 outline-offset-2 outline-dashed outline-primary'
							: ''
					]"
					@dragover.prevent="dropzoneDragover"
					@dragleave="dropzoneDragleave"
					@drop="dropzoneDrop"
				>
					<div class="shrink-0">
						<!-- Upload button -->
						<button
							class="context-menu-button btn btn-primary-light text-center rounded-full p-3"
							title="Možnosti"
							type="button"
							:disabled="messageIsSending"
							@click.prevent="openFileInput()"
						>
							<PaperClip class="size-6 p-1"></PaperClip>
						</button>

						<!-- File input -->
						<input
							ref="fileInput"
							type="file"
							class="hidden"
							accept=".jpeg,.jpg,.png,.pdf,.txt,.mp4"
							@change="handleFilesSelected"
						/>
					</div>
					<div class="grow flex flex-col px-4 min-w-px">
						<!-- Attachment -->
						<div v-if="attachment" class="flex gap-3 overflow-auto py-3 mb-2">
							<div class="relative">
								<InfoPill class="relative w-full truncate max-w-80!">
									<span>
										{{ attachment.name }}
									</span>
									<!-- Loading -->
									<Transition name="fade">
										<div
											class="absolute -inset-0.5 grid place-items-center bg-white/60"
											v-show="attachmentIsValidating"
										>
											<Spinner class="size-5 text-primary"></Spinner>
										</div>
									</Transition>
								</InfoPill>
								<button
									class="btn btn-secondary-link absolute end-0 top-0 p-0 translate-x-1/2 -translate-y-1/2 rounded-full"
									:disabled="messageIsSending"
									@click.prevent="removeAttachment()"
								>
									<DismissCircleFill class="size-5"></DismissCircleFill>
								</button>
							</div>
						</div>

						<!-- main text area -->
						<textarea
							v-model.trim="textValue"
							class="form-input field-sizing-content max-h-60 wrap-anywhere resize-none"
							rows="1"
							placeholder="Vaše zpráva..."
							@input="stickToBottom()"
							@keydown.enter="textareaEnterPressed"
						></textarea>
					</div>
					<div class="shrink-0">
						<!-- Send button -->
						<button
							class="relative btn btn-primary text-center rounded-full px-3"
							title="Odeslat"
							type="submit"
							:disabled="messageIsSending"
						>
							<span>
								<Send class="size-6 p-1"></Send>
							</span>
							<!-- Loading -->
							<Transition name="fade">
								<div
									v-if="messageIsSending"
									class="absolute -inset-0.5 grid place-items-center bg-white/60"
								>
									<Spinner class="size-8 text-primary"></Spinner>
								</div>
							</Transition>
						</button>
					</div>
				</div>
			</form>
		</template>
	</aside>
</template>

<script setup lang="ts">
import Send from '@/assets/icons/send.svg'
import Unlock from '@/assets/icons/unlock.svg'
import PaperClip from '@/assets/icons/paper-clip.svg'
import Spinner from '@/assets/icons/spinner.svg'
import InfoPill from './InfoPill.vue'
import DismissCircleFill from '@/assets/icons/dismiss-circle-fill.svg'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useChatStore } from '@/stores/chatStore'
const chatStore = useChatStore()
import signalRService from '@/services/signalr'
import { useChatScroll } from '@/composables/useChatScroll'
const { isAtBottom } = useChatScroll()
import { useToast } from 'vue-toastification'
const toast = useToast()

const textValue = ref<string>('')
const ATTACHMENT_SEND_TIMEOUT_MS = 10_000

/**
 * Send
 */
const messageIsSending = ref<boolean>(false)
let attachmentSendTimeout: ReturnType<typeof setTimeout> | null = null
let isSendingAttachment = false
let newMessageReceivedDuringAttachmentSend = false

const stopSendingIndicator = () => {
	messageIsSending.value = false
	isSendingAttachment = false
	newMessageReceivedDuringAttachmentSend = false

	if (attachmentSendTimeout) {
		clearTimeout(attachmentSendTimeout)
		attachmentSendTimeout = null
	}
}

const onNewMessage = () => {
	if (!messageIsSending.value) return

	if (isSendingAttachment) {
		newMessageReceivedDuringAttachmentSend = true
		return
	}

	stopSendingIndicator()
}

onMounted(() => {
	void signalRService.on('NewMessage', onNewMessage)
})

onBeforeUnmount(() => {
	stopSendingIndicator()
	void signalRService.off('NewMessage', onNewMessage)
})

const submit = async () => {
	if (messageIsSending.value || (!textValue.value && !attachment.value)) return

	const fileToSend = attachment.value
	const sendingAttachment = Boolean(fileToSend)
	messageIsSending.value = true
	isSendingAttachment = sendingAttachment
	newMessageReceivedDuringAttachmentSend = false

	try {
		if (fileToSend) {
			await chatStore.postAttachment(fileToSend, textValue.value)
		} else {
			await chatStore.postMessage(textValue.value)
		}

		clearInputs()
	} finally {
		if (!sendingAttachment) {
			stopSendingIndicator()
		} else {
			isSendingAttachment = false

			if (newMessageReceivedDuringAttachmentSend) {
				stopSendingIndicator()
			} else {
				attachmentSendTimeout = setTimeout(
					stopSendingIndicator,
					ATTACHMENT_SEND_TIMEOUT_MS
				)
			}
		}
	}
}

/**
 * Dropzone
 */
const isDragOver = ref<boolean>(false)
const fileInput = ref<HTMLInputElement | null>(null)
const attachment = ref<File | null>(null)

// dragover classes
const dropzoneDragover = (event: DragEvent) => {
	event.preventDefault()
	isDragOver.value = true
}

const dropzoneDragleave = () => {
	isDragOver.value = false
}

// files dropped -> add them to files
const dropzoneDrop = (event: DragEvent) => {
	event.preventDefault()
	isDragOver.value = false

	if (!event.dataTransfer) return

	const droppedFile = event.dataTransfer.files[0]

	if (!droppedFile) return

	attachment.value = droppedFile

	stickToBottom()
}

// file input browser trigger
const openFileInput = () => {
	fileInput.value?.click()
}

// files selected through file input browser
const handleFilesSelected = (e: Event) => {
	const input = e.target as HTMLInputElement
	if (!input.files) return

	const file = input.files[0]

	if (!file) return

	attachment.value = file
	stickToBottom()
}

// validate file with the server
const attachmentIsValidating = ref<boolean>(false)
watch(attachment, async (newValue) => {
	if (!newValue) return

	attachmentIsValidating.value = true

	try {
		const data = await chatStore.validateAttachment(
			newValue.type,
			newValue.name,
			newValue.size
		)

		if (!data.isValid) {
			toast.error(data.message || 'Soubor nemůže být nahrán')
			removeAttachment()
		}
	} catch (e) {
		removeAttachment()
	} finally {
		attachmentIsValidating.value = false
	}
})

// removes file from attached list
const removeAttachment = () => {
	attachment.value = null

	if (!fileInput.value) return

	fileInput.value.value = ''
}

const clearInputs = () => {
	textValue.value = ''
	removeAttachment()
}

const textareaEnterPressed = (e: KeyboardEvent) => {
	if (e.shiftKey) {
		// shift is pressed - make new line
		return
	}

	e.preventDefault()
	submit()
}

const stickToBottom = () => {
	if (isAtBottom.value) {
		// fixes resizeable MessengerInput component
		// prevents messenger for shifting the scroll up
		// when scroll is at bottom and inputs resizes
		chatStore.handleScroll(false, false)
	}
}
</script>

<style scoped></style>
